import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Badge } from '../components/Common';
import {
  useCancellation,
  useSuggestions,
  useUpdateCancellationStatus,
  useRegenerateSuggestions,
} from '../hooks/useCancellations';
import type { Suggestion, Cancellation } from '../types';
import { format, parseISO } from 'date-fns';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface SuggestionCardProps {
  suggestion: Suggestion;
  rank: number;
  cancellation: Cancellation;
}

function SuggestionCard({ suggestion, rank, cancellation }: SuggestionCardProps) {
  const handleCall = () => {
    if (suggestion.physio_phone) {
      window.location.href = `tel:${suggestion.physio_phone}`;
    }
  };

  // Build URL for viewing this physio's schedule with the cancelled patient highlighted
  const scheduleUrl = `/map?physioId=${suggestion.physio_id}&date=${format(parseISO(suggestion.suggested_time), 'yyyy-MM-dd')}&patientId=${cancellation.patient_id}&time=${encodeURIComponent(suggestion.suggested_time)}&patientName=${encodeURIComponent(cancellation.patient_name)}&cancellationId=${cancellation.id}&suggestionId=${suggestion.id}`;

  return (
    <div className="p-3 sm:p-4 bg-white border border-gray-200 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start space-x-3 sm:space-x-4">
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-700 rounded-full font-bold text-sm">
            {rank}
          </div>
          <div className="min-w-0">
            <h4 className="font-medium text-gray-900">{suggestion.physio_name}</h4>
            <p className="text-sm text-gray-500">{suggestion.physio_postcode}</p>
            {suggestion.physio_specialisms.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {suggestion.physio_specialisms.map((spec) => (
                  <Badge key={spec} variant="info">
                    {spec}
                  </Badge>
                ))}
              </div>
            )}
            {suggestion.missing_capability_names && suggestion.missing_capability_names.length > 0 && (
              <p className="mt-2 text-sm text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                Missing capability: {suggestion.missing_capability_names.join(', ')}
              </p>
            )}
          </div>
        </div>
        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:text-right pl-11 sm:pl-0">
          <div className="text-base sm:text-lg font-bold text-primary-600">Score: {suggestion.score}</div>
          {suggestion.distance_km !== null && (
            <p className="text-sm text-gray-500">
              {suggestion.distance_km.toFixed(1)} km
              {suggestion.travel_time_mins !== null && (
                <span> ({suggestion.travel_time_mins} min)</span>
              )}
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-500">
          Suggested: {formatDate(suggestion.suggested_time)}
        </p>
        <div className="flex items-center space-x-2">
          <Link to={scheduleUrl} className="flex-1 sm:flex-none">
            <Button size="sm" variant="secondary" className="w-full sm:w-auto">
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                />
              </svg>
              <span className="hidden sm:inline">View </span>Schedule
            </Button>
          </Link>
          {suggestion.physio_phone && (
            <Button size="sm" onClick={handleCall} className="flex-1 sm:flex-none">
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Call
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CancellationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cancellationId = parseInt(id || '0');

  const { data: cancellation, isLoading: cancellationLoading } = useCancellation(cancellationId);
  const { data: suggestions, isLoading: suggestionsLoading } = useSuggestions(cancellationId);
  const updateStatus = useUpdateCancellationStatus();
  const regenerate = useRegenerateSuggestions();

  const handleStatusUpdate = async (status: 'resolved' | 'failed') => {
    await updateStatus.mutateAsync({ id: cancellationId, status });
    navigate('/');
  };

  const handleRegenerate = async () => {
    await regenerate.mutateAsync(cancellationId);
  };

  if (cancellationLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!cancellation) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-gray-500">Cancellation not found</p>
      </div>
    );
  }

  const statusVariant =
    cancellation.status === 'pending'
      ? 'warning'
      : cancellation.status === 'resolved'
        ? 'success'
        : 'danger';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex-shrink-0 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Cancellation Details</h1>
        </div>
        <Badge variant={statusVariant}>{cancellation.status}</Badge>
      </div>

      {/* Cancellation Info */}
      <Card title="Appointment Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-500">Patient</p>
            <p className="text-lg font-medium text-gray-900">{cancellation.patient_name}</p>
            {cancellation.patient_postcode && (
              <p className="text-sm text-gray-500">{cancellation.patient_postcode}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Original Time</p>
            <p className="text-lg font-medium text-gray-900">
              {formatDate(cancellation.original_time)}
            </p>
          </div>
          {cancellation.original_physio_name && (
            <div>
              <p className="text-sm font-medium text-gray-500">Original Physio</p>
              <p className="text-lg font-medium text-gray-900">
                {cancellation.original_physio_name}
              </p>
            </div>
          )}
          {cancellation.reason && (
            <div>
              <p className="text-sm font-medium text-gray-500">Reason</p>
              <p className="text-lg font-medium text-gray-900">{cancellation.reason}</p>
            </div>
          )}
          {cancellation.reported_by && (
            <div>
              <p className="text-sm font-medium text-gray-500">Reported By</p>
              <p className="text-lg font-medium text-gray-900">{cancellation.reported_by}</p>
            </div>
          )}
          {cancellation.specialisms_needed.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-500">Required Specialisms</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {cancellation.specialisms_needed.map((spec) => (
                  <Badge key={spec} variant="info">
                    {spec}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Suggestions */}
      <Card
        title="Suggested Replacements"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRegenerate}
            isLoading={regenerate.isPending}
          >
            Regenerate
          </Button>
        }
      >
        {suggestionsLoading ? (
          <p className="text-gray-500">Loading suggestions...</p>
        ) : !suggestions || suggestions.length === 0 ? (
          <p className="text-gray-500">
            No suggestions available. Try regenerating or check patient constraints.
          </p>
        ) : (
          <div className="space-y-4">
            {suggestions.map((suggestion, index) => (
              <SuggestionCard key={suggestion.id} suggestion={suggestion} rank={index + 1} cancellation={cancellation} />
            ))}
          </div>
        )}
      </Card>

      {/* Actions */}
      {cancellation.status === 'pending' && (
        <Card title="Actions">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
            <Button
              onClick={() => handleStatusUpdate('resolved')}
              isLoading={updateStatus.isPending}
              className="w-full sm:w-auto"
            >
              Mark as Resolved
            </Button>
            <Button
              variant="danger"
              onClick={() => handleStatusUpdate('failed')}
              isLoading={updateStatus.isPending}
              className="w-full sm:w-auto"
            >
              Mark as Failed
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
